import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { CheckCircle2, ChevronLeft, ChevronRight, ShieldCheck, Wallet } from 'lucide-react';

/**
 * PayoutProofSlideshow
 * A luxury carousel of real payout screenshots (uploaded WhatsApp / bank proofs).
 * Auto-rotates, supports manual nav, tap-to-enlarge in a lightbox.
 */
const PROOFS = [
  {
    src: 'https://customer-assets-0z36b82j.emergentagent.net/job_ca0be248-de92-4039-9e96-8b78dfde0e40/artifacts/zps2wefh_IMG-20260609-WA0001.jpg.jpeg',
    label: 'Indian Bank Payout',
    caption: 'UPI · Verified',
  },
  {
    src: 'https://customer-assets-0z36b82j.emergentagent.net/job_ca0be248-de92-4039-9e96-8b78dfde0e40/artifacts/7etm3zx7_IMG-20260609-WA0002.jpg.jpeg',
    label: 'Trader Withdrawal',
    caption: 'Bank Transfer · Verified',
  },
  {
    src: 'https://customer-assets-0z36b82j.emergentagent.net/job_ca0be248-de92-4039-9e96-8b78dfde0e40/artifacts/8zypr6vz_IMG-20260609-WA0003.jpg.jpeg',
    label: 'Funded Trader Payout',
    caption: 'Ayronn Desk · Verified',
  },
  {
    src: 'https://customer-assets-0z36b82j.emergentagent.net/job_ca0be248-de92-4039-9e96-8b78dfde0e40/artifacts/ytp1yzfm_IMG-20260609-WA0004.jpg.jpeg',
    label: 'Elite Payout',
    caption: 'Elite Desk · Verified',
  },
  {
    src: 'https://customer-assets-0z36b82j.emergentagent.net/job_ca0be248-de92-4039-9e96-8b78dfde0e40/artifacts/ai7nzd82_IMG-20260609-WA0006.jpg.jpeg',
    label: 'Weekly Trader Payout',
    caption: 'UPI · Verified',
  },
];

const PayoutProofSlideshow = () => {
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: '-50px' });

  useEffect(() => {
    if (!inView || zoom) return;
    const id = setInterval(() => setI(p => (p + 1) % PROOFS.length), 4500);
    return () => clearInterval(id);
  }, [inView, zoom]);

  const prev = () => setI(p => (p - 1 + PROOFS.length) % PROOFS.length);
  const next = () => setI(p => (p + 1) % PROOFS.length);

  return (
    <section ref={ref} className="relative py-14 md:py-20 bg-gradient-to-b from-black via-[#020202] to-black overflow-hidden" data-testid="payout-proof-section">
      {/* subtle gold aura */}
      <div className="absolute left-1/2 top-16 -translate-x-1/2 w-[520px] h-[520px] bg-brand-gold/[0.05] blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-gold/30 bg-brand-gold/5 text-brand-gold text-[10px] font-bold uppercase tracking-[0.2em]">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
            Verified Payouts
          </div>
          <h2 className="mt-5 text-4xl md:text-5xl font-black font-heading text-white tracking-[-0.02em]">
            Real money. <span className="text-brand-gold">Real traders.</span> Real proof.
          </h2>
          <p className="mt-3 text-sm text-zinc-500 max-w-xl mx-auto">
            Screenshots from Ayronn traders showing actual payouts to Indian banks & UPI accounts. Personal details redacted.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-6 lg:gap-10 items-center">
          {/* Slideshow frame */}
          <div className="relative">
            {/* gradient border shell */}
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-brand-gold/25 via-white/10 to-brand-primary/20 p-[1.5px]">
              <div className="relative rounded-[15px] bg-[#0A0A0A] p-3 sm:p-4">
                {/* image */}
                <button
                  type="button"
                  onClick={() => setZoom(true)}
                  className="group relative block w-full aspect-[3/4] sm:aspect-[4/3] rounded-xl overflow-hidden bg-black cursor-zoom-in"
                  data-testid="payout-proof-image-btn"
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={PROOFS[i].src}
                      src={PROOFS[i].src}
                      alt={PROOFS[i].label}
                      initial={{ opacity: 0, scale: 1.02 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 w-full h-full object-contain bg-[#050505]"
                      loading="lazy"
                    />
                  </AnimatePresence>
                  {/* verified stamp */}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-md border border-brand-positive/40">
                    <ShieldCheck className="w-3 h-3 text-brand-positive" />
                    <span className="text-[9px] font-black tracking-wider uppercase text-brand-positive">Verified</span>
                  </div>
                  {/* corner label */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-gold font-bold">{PROOFS[i].label}</div>
                      <div className="text-[10px] text-zinc-400">{PROOFS[i].caption}</div>
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500 bg-black/60 px-2 py-0.5 rounded-md border border-white/10">{i + 1} / {PROOFS.length}</span>
                  </div>
                </button>

                {/* controls */}
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {PROOFS.map((_, k) => (
                      <button
                        key={k}
                        onClick={() => setI(k)}
                        data-testid={`payout-proof-dot-${k}`}
                        aria-label={`Show payout proof ${k + 1}`}
                        className={`h-1.5 rounded-full transition-all ${k === i ? 'w-8 bg-brand-gold' : 'w-3 bg-white/15 hover:bg-white/30'}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={prev}
                      data-testid="payout-proof-prev"
                      aria-label="Previous proof"
                      className="p-2 rounded-lg bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={next}
                      data-testid="payout-proof-next"
                      aria-label="Next proof"
                      className="p-2 rounded-lg bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Side trust column */}
          <div className="space-y-3">
            {[
              { icon: Wallet, title: 'Direct to Indian Banks', body: 'All shown payouts sent via IMPS / UPI to real Indian bank accounts. No middleman.' },
              { icon: CheckCircle2, title: 'Personally Reviewed', body: 'Every screenshot verified against our internal payout ledger before publishing here.' },
              { icon: ShieldCheck, title: 'Privacy Protected', body: 'UTRs and bank details are masked to protect trader identity. Amounts are unedited.' },
            ].map((c, k) => (
              <div key={k} className="relative rounded-2xl border border-white/8 bg-white/[0.03] backdrop-blur-xl p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-gold/10 border border-brand-gold/25 flex items-center justify-center flex-shrink-0">
                  <c.icon className="w-5 h-5 text-brand-gold" strokeWidth={1.6} />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">{c.title}</div>
                  <div className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{c.body}</div>
                </div>
              </div>
            ))}
            <div className="text-[10.5px] text-zinc-500 pt-2 border-t border-white/5">
              Tap any image to enlarge. New proofs added weekly.
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setZoom(false)}
            data-testid="payout-proof-lightbox"
          >
            <motion.img
              key={PROOFS[i].src}
              src={PROOFS[i].src}
              alt={PROOFS[i].label}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-[92vw] max-h-[86vh] object-contain rounded-lg shadow-[0_25px_100px_rgba(212,175,55,0.15)]"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute top-5 right-5 text-white/80 hover:text-white text-2xl font-bold w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center"
              onClick={() => setZoom(false)}
              aria-label="Close preview"
              data-testid="payout-proof-lightbox-close"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PayoutProofSlideshow;
